import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import {
  BrowserRouter,
  Link,
  Outlet,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom'

import { useMe } from './lib/queries'
import { PATRON_CLIENTE, RUTA_CLIENTES } from './lib/rutas'
import AdminPage from './modules/admin'
import AdquisicionPage from './modules/adquisicion'
import ClientesPage from './modules/clientes'
import EntrevistasPage from './modules/entrevistas'
import OverviewPage from './modules/overview'
import RevenuePage from './modules/revenue'
import UsagePage from './modules/usage'
import {
  AppShell,
  navItemDe,
  navItemDeUbicacion,
  navItemsForRole,
  rolesDeRuta,
} from './shell'
import { AccessDenied, Card, Icon, Section, Stack } from './ui'

/**
 * Cliente de react-query del panel.
 *
 * Es una función y no una constante para que cada `App` pueda tener su caché:
 * con un singleton de módulo, el rol del primer test se filtraba a todos los
 * siguientes (`/api/me` quedaba cacheado 30s y las guardas veían admin siempre).
 * En la app hay un solo mount, así que el comportamiento es el mismo.
 */
export function crearQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: { staleTime: 30_000, refetchOnWindowFocus: false },
    },
  })
}

const clientePorDefecto = crearQueryClient()

/**
 * Layout de todas las rutas. El shell se monta **una vez** como ruta padre y el
 * módulo entra por el `Outlet`: así cambiar de sección no remonta la sidebar ni
 * el header, y el período elegido y los toasts sobreviven a la navegación (viven
 * en contextos del shell).
 *
 * El título del header sale de la tabla de navegación y no de un prop por ruta:
 * el nombre de la sección ya está declarado una vez en `NAV_ITEMS`. La ficha de
 * un registro (`/clientes/{id}`) sigue siendo su sección: el header dice
 * "Clientes", no "Pantalla no encontrada".
 */
function Layout() {
  const { pathname } = useLocation()
  const item = navItemDeUbicacion(pathname)

  return (
    <AppShell titulo={item?.label ?? 'Pantalla no encontrada'}>
      <Outlet />
    </AppShell>
  )
}

/**
 * Guarda de rol de una ruta.
 *
 * Existe para el caso de entrar **por URL**: la sidebar ya no muestra el link,
 * pero una dirección pegada en un chat sigue llegando. Sin esto, el módulo
 * montaría, pediría su payload y mostraría un error de carga genérico donde en
 * realidad hay una respuesta clara ("tu rol no ve esta sección").
 *
 * Los roles salen de `rolesDeRuta`, la misma tabla que filtra la navegación. Y
 * como el shell ya resolvió `/api/me` antes de renderizar el `Outlet`, `useMe()`
 * acá lee de la caché de react-query sin disparar un segundo request.
 *
 * No es seguridad: la API aplica `requireRole` sobre cada endpoint y recorta el
 * $$ con `stripMoney`. Esto evita pedir un payload que va a ser rechazado y
 * explica por qué.
 */
function Guardada({ path, children }: { path: string; children: ReactNode }) {
  const { data: user } = useMe()
  const roles = rolesDeRuta(path)
  const item = navItemDe(path)

  // El shell bloquea el render hasta resolver el usuario, así que acá siempre
  // está. El chequeo es por tipos, no por un estado alcanzable.
  if (user === undefined || roles === undefined || item === undefined) {
    return null
  }

  if (!roles.includes(user.rol)) {
    return (
      <AccessDenied seccion={item.label} permitidos={roles} rol={user.rol} />
    )
  }

  return <>{children}</>
}

/**
 * 404 dentro del shell, con la navegación a la vista: una pantalla de error que
 * deja encerrado obliga a volver con el botón del navegador. Ofrece las secciones
 * que **este** rol tiene, no las seis.
 */
function NoEncontrada() {
  const { pathname } = useLocation()
  const { data: user } = useMe()
  const disponibles = user === undefined ? [] : navItemsForRole(user.rol)

  return (
    <Section
      titulo="Esa pantalla no existe"
      bajada={
        <>
          No hay nada en <code className="font-mono text-text">{pathname}</code>.
          Puede ser un link viejo, o una dirección con un typo.
        </>
      }
      regla={false}
    >
      <Card
        as="section"
        ariaLabel="Secciones disponibles para tu cuenta"
        padding="lg"
        severidad="warning"
      >
        <Stack gap="sm" align="start">
          <p className="inline-flex items-center gap-2 text-meta text-muted-strong">
            <Icon name="critico" size={13} className="text-warning-text" />
            Estas son las secciones a las que tu cuenta entra:
          </p>
          <ul className="flex flex-wrap gap-1.5">
            {disponibles.map((item) => (
              <li key={item.to}>
                <Link
                  to={item.to}
                  className="inline-flex items-center gap-1.5 rounded border border-border bg-surface px-2 py-1 text-micro font-semibold text-text transition-colors duration-150 ease-brand hover:border-n300 hover:bg-n100"
                >
                  <Icon name={item.icon} size={11} />
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>
        </Stack>
      </Card>
    </Section>
  )
}

/**
 * Las siete rutas del panel, en el orden de la navegación.
 *
 * Cada una va envuelta en su guarda, incluidas las que ven los tres roles:
 * la guarda lee la tabla, así que si mañana Clientes pasara a ser solo de ops,
 * alcanza con cambiar `NAV_ITEMS` y el link y la guarda se mueven juntos.
 */
export interface AppProps {
  /** Cliente de react-query. Inyectable para que un test arranque con la caché
   *  vacía; en la app se usa el del módulo. */
  client?: QueryClient
}

export function App({ client = clientePorDefecto }: AppProps = {}) {
  return (
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <Routes>
          <Route element={<Layout />}>
            <Route
              path="/"
              element={
                <Guardada path="/">
                  <OverviewPage />
                </Guardada>
              }
            />
            <Route
              path="/revenue"
              element={
                <Guardada path="/revenue">
                  <RevenuePage />
                </Guardada>
              }
            />
            <Route
              path={RUTA_CLIENTES}
              element={
                <Guardada path={RUTA_CLIENTES}>
                  <ClientesPage />
                </Guardada>
              }
            >
              {/*
                La ficha de una cuenta vive en la URL: se puede refrescar,
                compartir y abrir desde /entrevistas o desde una alerta del
                Overview, que ya generaban esta dirección cuando el router no la
                declaraba.

                Va como ruta **hija** y no como una ruta hermana con el mismo
                elemento: así `ClientesPage` no se desmonta al abrir y cerrar el
                drawer, y la vista elegida (portfolio / brecha) y los filtros de
                la tabla sobreviven. El elemento de la hija es el `Outlet` por
                defecto y no dibuja nada — lo único que aporta es que la ruta
                **matchee** en vez de caer en el catch-all; el drawer lo monta la
                página leyendo el patrón.
              */}
              <Route path=":clientId" />
            </Route>
            {/*
              `/usage` va guardada como las demás aunque la abran los tres
              roles: la guarda lee `NAV_ITEMS`, así que el permiso se declara una
              sola vez. El corte de este módulo no es por ruta sino por campo, y
              lo aplica la API con `stripMoney`.
            */}
            <Route
              path="/usage"
              element={
                <Guardada path="/usage">
                  <UsagePage />
                </Guardada>
              }
            />
            <Route
              path="/adquisicion"
              element={
                <Guardada path="/adquisicion">
                  <AdquisicionPage />
                </Guardada>
              }
            />
            <Route
              path="/entrevistas"
              element={
                <Guardada path="/entrevistas">
                  <EntrevistasPage />
                </Guardada>
              }
            />
            <Route
              path="/admin"
              element={
                <Guardada path="/admin">
                  <AdminPage />
                </Guardada>
              }
            />
            <Route path="*" element={<NoEncontrada />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  )
}
